import { useEffect, useMemo, useRef, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import {
  MapLayoutMutationError,
  useAdminMapLayouts,
  usePublishMapLayout,
  useResetMapLayoutDraft,
  useSaveMapLayoutDraft,
} from "@/hooks/use-map-layout";
import type { MapLayoutValidationIssue } from "@/lib/office/map-layout-validate";
import { validateMapLayout } from "@/lib/office/map-layout-validate";
import {
  cloneMapLayout,
  DEFAULT_JOIN_MAP_ID,
  MAP_IDS,
  MAP_REGISTRY,
  type MapId,
  type MapLayoutDocument,
} from "@/lib/office/personal-office-layout";
import { cn } from "@/lib/utils";
import { MapLayoutCanvas } from "./MapLayoutCanvas";
import { MapLayoutInspector } from "./MapLayoutInspector";
import { type LayoutIssueRow, MapLayoutIssues } from "./MapLayoutIssues";
import { MapLayoutPalette } from "./MapLayoutPalette";
import {
  deleteItems,
  duplicateItems,
  type EditorItemRef,
  type EditorSelectionSet,
  moveItems,
  type PaletteItem,
} from "./map-layout-utils";

const MAP_LABELS: Record<string, string> = Object.fromEntries(
  MAP_REGISTRY.map((m) => [m.id, m.label]),
);

const HISTORY_CAP = 50;

function isTypingTarget(target: EventTarget | null): boolean {
  const tag = (target as HTMLElement | null)?.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

export function MapLayoutEditor() {
  const [mapId, setMapId] = useState<MapId>(DEFAULT_JOIN_MAP_ID as MapId);
  const { data, isLoading, isError, error, refetch } = useAdminMapLayouts(mapId);
  const saveDraft = useSaveMapLayoutDraft(mapId);
  const publish = usePublishMapLayout(mapId);
  const resetDraft = useResetMapLayoutDraft(mapId);

  const [layout, setLayout] = useState<MapLayoutDocument | null>(null);
  const [selection, setSelection] = useState<EditorSelectionSet>([]);
  const [palette, setPalette] = useState<PaletteItem | null>(null);
  const [dirty, setDirty] = useState(false);
  const [apiIssues, setApiIssues] = useState<MapLayoutValidationIssue[]>([]);

  const pastRef = useRef<MapLayoutDocument[]>([]);
  const futureRef = useRef<MapLayoutDocument[]>([]);
  const [historyTick, setHistoryTick] = useState(0);

  // Switching maps discards unsaved local edits for the previous map.
  useEffect(() => {
    // Depend on mapId intentionally — reset all local editor state for the new map.
    void mapId;
    setDirty(false);
    setSelection([]);
    setPalette(null);
    setLayout(null);
    setApiIssues([]);
    pastRef.current = [];
    futureRef.current = [];
    setHistoryTick((t) => t + 1);
  }, [mapId]);

  useEffect(() => {
    if (data?.draft.layout && !dirty) {
      setLayout(cloneMapLayout(data.draft.layout));
      pastRef.current = [];
      futureRef.current = [];
      setHistoryTick((t) => t + 1);
    }
  }, [data, dirty]);

  const liveValidation = useMemo(() => {
    if (!layout) return null;
    return validateMapLayout({ ...layout, id: mapId });
  }, [layout, mapId]);

  const liveIssues: LayoutIssueRow[] = useMemo(() => {
    if (!liveValidation || liveValidation.ok) return [];
    return liveValidation.issues;
  }, [liveValidation]);

  const displayIssues: LayoutIssueRow[] = useMemo(() => {
    // Prefer live validation; merge any API-only issues (owned offices) that aren't in live.
    if (liveIssues.length > 0) return liveIssues;
    return apiIssues.map((i) =>
      i.path === "ownedOffices" ? { ...i, severity: "warning" as const } : i,
    );
  }, [liveIssues, apiIssues]);

  const issueSelections = useMemo(
    () => displayIssues.map((i) => i.selection).filter((s): s is NonNullable<typeof s> => !!s),
    [displayIssues],
  );

  const canUndo = historyTick >= 0 && pastRef.current.length > 0;
  const canRedo = historyTick >= 0 && futureRef.current.length > 0;
  void historyTick;

  function pushPast(snapshot: MapLayoutDocument) {
    pastRef.current = [...pastRef.current, cloneMapLayout(snapshot)].slice(-HISTORY_CAP);
    futureRef.current = [];
    setHistoryTick((t) => t + 1);
  }

  /** Discrete edit: record undo from current layout, apply next. */
  function commitEdit(next: MapLayoutDocument) {
    if (layout) pushPast(layout);
    setLayout(next);
    setDirty(true);
    setApiIssues([]);
  }

  /** Live drag update without history. */
  function liveChange(next: MapLayoutDocument) {
    setLayout(next);
    setDirty(true);
    setApiIssues([]);
  }

  /** After drag/resize: record the pre-gesture snapshot for undo. */
  function onGestureEnd(before: MapLayoutDocument) {
    pushPast(before);
  }

  function undo() {
    if (!layout || pastRef.current.length === 0) return;
    const prev = pastRef.current[pastRef.current.length - 1];
    if (!prev) return;
    pastRef.current = pastRef.current.slice(0, -1);
    futureRef.current = [...futureRef.current, cloneMapLayout(layout)].slice(-HISTORY_CAP);
    setLayout(cloneMapLayout(prev));
    setDirty(true);
    setHistoryTick((t) => t + 1);
  }

  function redo() {
    if (!layout || futureRef.current.length === 0) return;
    const next = futureRef.current[futureRef.current.length - 1];
    if (!next) return;
    futureRef.current = futureRef.current.slice(0, -1);
    pastRef.current = [...pastRef.current, cloneMapLayout(layout)].slice(-HISTORY_CAP);
    setLayout(cloneMapLayout(next));
    setDirty(true);
    setHistoryTick((t) => t + 1);
  }

  function selectFromIssue(ref: EditorItemRef) {
    setSelection([ref]);
    setPalette(null);
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (isTypingTarget(e.target)) return;

      const mod = e.metaKey || e.ctrlKey;

      if (e.key === "Escape") {
        setPalette(null);
        setSelection([]);
        return;
      }

      if (mod && e.key.toLowerCase() === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      if (mod && (e.key.toLowerCase() === "y" || (e.key.toLowerCase() === "z" && e.shiftKey))) {
        e.preventDefault();
        redo();
        return;
      }
      if (mod && e.key.toLowerCase() === "d" && layout && selection.length > 0) {
        e.preventDefault();
        const result = duplicateItems(layout, selection);
        commitEdit(result.layout);
        setSelection(result.selection);
        return;
      }

      if ((e.key === "Delete" || e.key === "Backspace") && selection.length > 0 && layout) {
        e.preventDefault();
        commitEdit(deleteItems(layout, selection));
        setSelection([]);
        return;
      }

      if (layout && selection.length > 0) {
        const step = e.shiftKey ? 5 : 1;
        let dx = 0;
        let dy = 0;
        if (e.key === "ArrowLeft") dx = -step;
        else if (e.key === "ArrowRight") dx = step;
        else if (e.key === "ArrowUp") dy = -step;
        else if (e.key === "ArrowDown") dy = step;
        if (dx !== 0 || dy !== 0) {
          e.preventDefault();
          commitEdit(moveItems(layout, selection, dx, dy));
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const switchMap = (next: MapId) => {
    if (next === mapId) return;
    if (dirty && !window.confirm("Discard unsaved changes and switch map?")) return;
    setMapId(next);
  };

  const layoutValid = liveValidation?.ok === true;

  const header = (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0 flex-1 space-y-3">
        <div>
          <h1 className="font-display text-lg font-semibold">Map layout</h1>
          <p className="text-sm text-muted-foreground">
            Edit and publish walkable maps.{" "}
            {mapId === "campus"
              ? "Campus outdoor map — place buildings from the palette, drag to position, Publish when ready."
              : mapId === "reception" || mapId === "cafe"
                ? "Commons building — furniture and zones (no personal offices)."
                : "Department floor — personal offices, furniture, and lobby exit."}
            {data ? ` · published v${data.published.version}` : null}
            {dirty ? " · unsaved changes" : null}
          </p>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Shift/⌘-click multi-select · marquee on empty · drag group · SE resize · ⌘Z undo · ⌘D
            duplicate · arrows nudge · Del
          </p>
        </div>

        <div
          role="tablist"
          aria-label="Select map to edit"
          className="inline-flex rounded-lg border border-border bg-muted/40 p-1"
        >
          {MAP_IDS.map((id) => {
            const active = mapId === id;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => switchMap(id)}
                className={cn(
                  "rounded-md px-4 py-2 text-sm font-semibold transition-colors",
                  active
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {MAP_LABELS[id]}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!layout || !canUndo}
          onClick={() => undo()}
          title="Undo (⌘Z)"
        >
          Undo
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!layout || !canRedo}
          onClick={() => redo()}
          title="Redo (⌘⇧Z)"
        >
          Redo
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!layout || resetDraft.isPending || !dirty}
          onClick={() => {
            if (data) {
              setLayout(cloneMapLayout(data.published.layout));
              setDirty(false);
              setSelection([]);
              setApiIssues([]);
              pastRef.current = [];
              futureRef.current = [];
              setHistoryTick((t) => t + 1);
            }
          }}
        >
          Discard
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!layout || resetDraft.isPending}
          onClick={() => {
            resetDraft.mutate(undefined, {
              onSuccess: (dto) => {
                if (dto) {
                  setLayout(cloneMapLayout(dto.layout));
                  setDirty(false);
                  setApiIssues([]);
                  pastRef.current = [];
                  futureRef.current = [];
                  setHistoryTick((t) => t + 1);
                }
              },
            });
          }}
        >
          Reset to published
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={!layout || saveDraft.isPending}
          onClick={() => {
            if (!layout) return;
            saveDraft.mutate(
              { ...layout, id: mapId },
              {
                onSuccess: () => {
                  setDirty(false);
                  setApiIssues([]);
                },
                onError: (err) => {
                  if (err instanceof MapLayoutMutationError) {
                    setApiIssues(err.issues);
                  }
                },
              },
            );
          }}
        >
          {saveDraft.isPending ? "Saving…" : "Save draft"}
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={!layout || publish.isPending || !layoutValid}
          title={
            !layoutValid
              ? `${displayIssues.length} issue(s) — fix them before publishing`
              : "Publish to live world"
          }
          onClick={() => {
            if (!layout) return;
            const place = MAP_LABELS[mapId];
            if (
              !window.confirm(
                mapId === "hq"
                  ? "Publish this map to the live HQ for everyone? Owned offices that would be removed will block publish."
                  : `Publish ${place} to the live world for everyone?`,
              )
            ) {
              return;
            }
            publish.mutate(
              { ...layout, id: mapId },
              {
                onSuccess: () => {
                  setDirty(false);
                  setApiIssues([]);
                },
                onError: (err) => {
                  if (err instanceof MapLayoutMutationError) {
                    setApiIssues(err.issues);
                  }
                },
              },
            );
          }}
        >
          {publish.isPending
            ? "Publishing…"
            : !layoutValid
              ? `Publish (${displayIssues.length})`
              : "Publish"}
        </Button>
      </div>
    </div>
  );

  let body: React.ReactNode;
  if (isError) {
    body = (
      <ErrorState
        message={error?.message ?? "Failed to load map layout"}
        onRetry={() => refetch()}
      />
    );
  } else if (isLoading || !layout) {
    body = (
      <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
        Loading {MAP_LABELS[mapId]}…
      </div>
    );
  } else {
    body = (
      <>
        <MapLayoutIssues issues={displayIssues} onSelect={selectFromIssue} />
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[200px_1fr_220px]">
          <div className="min-h-0 overflow-hidden rounded-md border border-border bg-card">
            <MapLayoutPalette
              outdoor={mapId === "campus"}
              palette={palette}
              onSelect={(item) => {
                setPalette(item);
                if (item) setSelection([]);
              }}
            />
          </div>
          <MapLayoutCanvas
            layout={layout}
            selection={selection}
            palette={palette}
            issueSelections={issueSelections}
            onEdit={commitEdit}
            onLiveChange={liveChange}
            onGestureEnd={onGestureEnd}
            onSelect={(sel) => {
              setSelection(sel);
              if (sel.length > 0) setPalette(null);
            }}
          />
          <div className="min-h-0 overflow-y-auto rounded-md border border-border bg-card">
            <MapLayoutInspector
              layout={layout}
              selection={selection}
              onChange={commitEdit}
              onDelete={() => {
                commitEdit(deleteItems(layout, selection));
                setSelection([]);
              }}
              onDuplicate={() => {
                const result = duplicateItems(layout, selection);
                commitEdit(result.layout);
                setSelection(result.selection);
              }}
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {header}
      {body}
    </div>
  );
}
